// Tenant bootstrap mechanism
// Resolves tenant from hostname and provides tenant config

import { ServiceLayerClient, type TenantConfig } from '@appic/service-layer'

let cachedTenant: TenantConfig | null = null
let tenantPromise: Promise<TenantConfig> | null = null

/**
 * Bootstrap tenant resolution
 * Reads window.location.host and resolves tenant via service-layer
 */
export async function bootstrapTenant(): Promise<TenantConfig> {
  // Return cached tenant if available
  if (cachedTenant) {
    return cachedTenant
  }

  // Return existing promise if bootstrap is in progress
  if (tenantPromise) {
    return tenantPromise
  }

  // Create new bootstrap promise
  tenantPromise = (async () => {
    try {
      const host = window.location.host
      // Use /service proxy (same-origin via nginx) instead of direct api.clanda.de call
      // This avoids CORS and allows cookie-based auth to work
      const serviceBasePath = import.meta.env.VITE_SERVICE_LAYER_BASE_URL || '/service'
      // baseUrl must include /v1 because ServiceLayerClient.resolveTenant() uses new URL('/v1/tenant/resolve', baseUrl)
      // which replaces the path, not appends to it. So baseUrl should be 'https://portal.clanda.de/service/v1'
      const baseUrl = typeof window !== 'undefined'
        ? `${window.location.origin}${serviceBasePath}/v1`
        : import.meta.env.VITE_SERVICE_LAYER_URL || 'https://api.clanda.de/v1'

      // No getToken — resolveTenant is a public pre-auth endpoint.
      // Sending a stale Supabase bearer token causes the server to attempt
      // bearer auth (which fails) instead of allowing unauthenticated access.
      const client = new ServiceLayerClient({ baseUrl })

      const tenant = await client.resolveTenant(host)
      cachedTenant = tenant
      return tenant
    } catch (error) {
      console.error('Failed to bootstrap tenant:', error)
      // Return default tenant config on error
      const host = window.location.host
      return {
        tenant_id: 'default',
        slug: 'default',
        primary_domain: host,
        brand_config: {
          logo: {
            light: '/logo-light.svg',
            dark: '/logo-dark.svg',
          },
          colors: {
            primary: '#0072BC',
            accent: '#005A96',
          },
          texts: {
            companyName: 'Appic Command',
            tagline: 'Supplier Onboarding System',
          },
        },
      } as TenantConfig
    } finally {
      tenantPromise = null
    }
  })()

  return tenantPromise
}

/**
 * Get current tenant config (synchronous)
 * Returns cached tenant or null if not bootstrapped
 */
export function getTenant(): TenantConfig | null {
  return cachedTenant
}

/**
 * Clear tenant cache (useful for testing or tenant switching)
 */
export function clearTenantCache(): void {
  cachedTenant = null
  tenantPromise = null
}

